import { Alert, Box, Button, Divider, Fade, FormControlLabel, Grid, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import Color from 'color';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import i18n from 'i18next';
import RotatingLoader from '../ui/RotatingLoader';
import { OrderPageTypography } from '../orderPage/themedComponents/OrderPageTypography';
import { OrderPagePriceBreakdown } from '../orderPage/components/OrderPagePriceBreakdown';
import { bookingFeeAmount, isBookingFeeOn } from '../orderPage/core/pricing';
import {
  CodesService,
  CustomPersonalDetailData,
  CustomPersonalDetailTypes,
  Language,
  PaymentLinkPaymentStatus,
  PaymentLinkResponseDTO,
  PaymentLinkService,
  PaymentLinkType,
  Tip,
  TipType,
} from '../api';
import PaymentIframe from '../orderPage/steps/PaymentIframe';
import { priceFormatterFromCurrency } from '../orderPage/utils';
import { OrderPageTextField } from '../orderPage/themedComponents/OrderPageTextField';
import { TipComponent } from '../orderPage/steps/payments/TipComponent';
import { parseSquarePaymentForm } from '../orderPage/steps/payments/squarePaymentForm';
import { useStripeLinkRenderProps } from '../orderPage/steps/payments/useStripeRenderProps';
import { TermsConditionsCheckbox } from '../orderPage/steps/personalDetails/TermsConditionsCheckbox';
import { FormStyledMenuItem } from '../orderPage/steps/personalDetails/RenderDropDownDetail';
import { OrderPageCheckbox } from '../orderPage/themedComponents/OrderPageCheckbox';
import { OrderPageSelect } from '../orderPage/themedComponents/OrderPageSelect';
import { getOrderPage } from '../utils/AccountUtils';
import { useLanguage } from '../utils/GeneralUtils';
import { AccountLogo } from '../shared/ui/AccountLogo';
import { safeStartSessionRecording } from '../utils/safePosthog';
import { applyCodeErrorMessage } from '../shared/errors/applyCodeErrors';

enum Errors {
  NO_ERROR,
  NOT_FOUND,
  ALREADY_PAID,
  ARCHIVED,
  EXPIRED,
}

type PaymentLinksWizardProps = {
  setLanguages: (languages: Language[]) => void;
};

export const PaymentLinksWizard = ({ setLanguages }: PaymentLinksWizardProps) => {
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { t } = useTranslation();
  const language = useLanguage();
  const [paymentLinkResponse, setPaymentLinkResponse] = useState<PaymentLinkResponseDTO | undefined>();
  const [currentStep, setCurrentStep] = useState<0 | 1>(0);
  const [termsChecked, setTermsChecked] = useState(false);
  const [boolFieldsChecked, setBoolFieldsChecked] = useState<Record<string, boolean>>({});
  const [selectedMaxPayments, setSelectedMaxPayments] = useState(1);
  const [shouldShowPaymentsSelector, setShouldShowPaymentsSelector] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardError, setGiftCardError] = useState<string | null>(null);
  const [isApplyingGiftCard, setIsApplyingGiftCard] = useState(false);
  const [isPaidWithGiftCard, setIsPaidWithGiftCard] = useState(false);

  const changeLang = (lang: any) => {
    void i18n.changeLanguage(lang);
    document.body.dir = i18n.dir();
    theme.direction = i18n.dir();
  };

  const account = paymentLinkResponse?.account;
  const { stripeAccountId, stripe_integration_publishable_key } = useStripeLinkRenderProps(
    account,
    paymentLinkResponse?.payment_link.link,
  );
  const orderPage = getOrderPage(account, paymentLinkResponse?.payment_link.order_page_id ?? '');
  const mandatoryBoolFields: CustomPersonalDetailData[] =
    orderPage?.custom_personal_details?.filter(
      (x: CustomPersonalDetailData) => x.errorLabel && x.type === CustomPersonalDetailTypes.BOOLEAN,
    ) ?? [];
  const allChecked =
    termsChecked && mandatoryBoolFields.every((f: CustomPersonalDetailData) => boolFieldsChecked[f.id]);
  const paymentTitle =
    paymentLinkResponse?.payment_link.payment_link_type === PaymentLinkType.MAIN_PAYMENT
      ? account?.activity_label_singular?.[language]
      : paymentLinkResponse?.payment_link.title;

  const shouldEnablePaymentsSelection = !!(
    account?.payment_integration_settings?.max_payments &&
    account?.payment_integration_settings?.min_amount_for_payments &&
    account.payment_integration_settings.min_amount_for_payments <=
      (paymentLinkResponse?.payment_link.amount_due ?? paymentLinkResponse?.payment_link.price ?? 0)
  );

  useEffect(() => {
    if (account?.customer_facing_widgets_languages) {
      setLanguages(account.customer_facing_widgets_languages);
    }
    if (account?.default_system_language) {
      changeLang(account.default_system_language);
    }
    const initial: Record<string, boolean> = {};
    mandatoryBoolFields.forEach((f: CustomPersonalDetailData) => {
      initial[f.id] = false;
    });
    setBoolFieldsChecked(initial);
  }, [paymentLinkResponse]);

  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);
  const [remainingMilliseconds, setRemainingMilliseconds] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(Errors.NO_ERROR);
  const paymentLinkId = searchParams.get('linkid');
  const [tip, setTip] = useState<Tip | undefined>(undefined);
  const setTipInternal = (type: TipType, value: number) => {
    setTip({ type, value });
  };

  useEffect(() => {
    safeStartSessionRecording(posthog);
  }, [posthog]);

  const getPaymentLink = async (maxPayments?: number) => {
    if (paymentLinkId) {
      setLoadingPaymentLink(true);
      try {
        const retrievedPaymentLink = await PaymentLinkService.buildPaymentLink({
          payment_link_id: paymentLinkId,
          tip,
          ...(maxPayments && maxPayments > 1 ? { max_payments: maxPayments } : {}),
        });
        if (retrievedPaymentLink.expired) {
          setError(Errors.EXPIRED);
        } else if (retrievedPaymentLink.payment_link.payment_status === PaymentLinkPaymentStatus.PAID) {
          setError(Errors.ALREADY_PAID);
        } else if (retrievedPaymentLink.archived) {
          setError(Errors.ARCHIVED);
        } else {
          setPaymentLinkResponse(retrievedPaymentLink);
        }
      } catch (e) {
        setError(Errors.NOT_FOUND);
        console.error(e);
      }
      setLoadingPaymentLink(false);
    }
  };

  useEffect(() => {
    void getPaymentLink();
  }, [paymentLinkId, tip]);

  const expiresAt = paymentLinkResponse?.payment_link.expires_at;
  useEffect(() => {
    if (!expiresAt) {
      setRemainingMilliseconds(null);
      return undefined;
    }
    const updateRemainingTime = () => {
      const remaining = moment.utc(expiresAt).diff(moment());
      if (remaining <= 0) {
        setRemainingMilliseconds(null);
        setError(Errors.EXPIRED);
        return true;
      }
      setRemainingMilliseconds(remaining);
      return false;
    };
    if (updateRemainingTime()) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      if (updateRemainingTime()) {
        clearInterval(intervalId);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt]);

  function getErrorText(error: Errors): string {
    switch (error) {
      case Errors.NOT_FOUND:
        return t('paymentLinks.errors.notfounc');
      case Errors.ALREADY_PAID:
        return t('paymentLinks.errors.alreadypaid');
      case Errors.ARCHIVED:
        return t('paymentLinks.errors.archived');
      case Errors.EXPIRED:
        return t('paymentLinks.errors.expired');
      case Errors.NO_ERROR:
      default:
        return 'No error.';
    }
  }

  const applyGiftCard = async () => {
    if (!paymentLinkResponse) {
      return;
    }
    setIsApplyingGiftCard(true);
    setGiftCardError(null);
    try {
      const resp = await CodesService.applyCode({
        entity_id: paymentLinkResponse.payment_link.id,
        code: giftCardCode.trim(),
      });
      if (resp.new_price === 0) {
        setIsPaidWithGiftCard(true);
      } else {
        setPaymentLinkResponse({
          ...paymentLinkResponse,
          payment_link: {
            ...paymentLinkResponse.payment_link,
            link: resp.payment_link ?? paymentLinkResponse.payment_link.link,
            amount_due: resp.new_price,
          },
        });
      }
    } catch (e: any) {
      setGiftCardError(applyCodeErrorMessage(e, t));
    }
    setIsApplyingGiftCard(false);
  };

  function calculateTip(tip?: Tip, price?: number) {
    if (!tip || !price) {
      return 0;
    }
    if (tip.type === TipType.FIXED) {
      return tip.value;
    }
    return (price * tip.value) / 100;
  }

  const paymentTimerDuration =
    remainingMilliseconds !== null ? moment.duration(remainingMilliseconds, 'milliseconds') : null;
  const isExpired = error === Errors.EXPIRED;
  const isBookingFeeActive = isBookingFeeOn(account);
  const priceFractionDigits = isBookingFeeActive ? 2 : 0;
  const remainingBalance = paymentLinkResponse?.payment_link.amount_due ?? paymentLinkResponse?.payment_link.price ?? 0;
  const tipAmount = calculateTip(tip, remainingBalance);
  const bookingFee = bookingFeeAmount(remainingBalance, account);

  const paymentTimerBox = paymentTimerDuration && (
    <Grid item container my={1}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          textAlign: 'center',
          borderRadius: 1.25,
          py: 0.25,
          px: 2,
          flex: 1,
          bgcolor: Color(theme.customTheme.palette.text.primary).alpha(0.2).string(),
        }}>
        <Typography variant="body1">
          {t('paymentTimer.prefix')}{' '}
          <strong>
            {t('paymentTimer.timeBold', {
              timeMinutes: `${paymentTimerDuration.minutes().toString().padStart(2, '0')}:${paymentTimerDuration
                .seconds()
                .toString()
                .padStart(2, '0')}`,
            })}{' '}
            {t('duration.min')}{' '}
          </strong>{' '}
          {t('paymentTimer.suffix')}
        </Typography>
      </Box>
    </Grid>
  );

  if (loadingPaymentLink) {
    return (
      <Grid container height="500px">
        <RotatingLoader />
      </Grid>
    );
  }

  if (currentStep === 0) {
    return (
      <Grid
        container
        flexDirection="column"
        alignItems="center"
        p={isSmallScreen ? 2 : 4}
        sx={{ maxWidth: '520px', mx: 'auto' }}>
        <Grid container flexDirection="column" alignItems="center" gap={2} mb={4}>
          <AccountLogo account={account} width={100} style={{ width: 100 }} alt="" />
          {paymentTitle && (
            <OrderPageTypography variant="h3">
              {t('paymentLinks.paymentFor')} {paymentTitle}
            </OrderPageTypography>
          )}
          {paymentLinkResponse?.payment_link.price != null &&
            (isBookingFeeActive ? (
              <Grid container item>
                <OrderPagePriceBreakdown
                  rows={[
                    { label: t('paymentLinks.remainingBalance'), amount: remainingBalance },
                    ...(tipAmount > 0 ? [{ label: t('order_page.gratuity'), amount: tipAmount }] : []),
                    { label: t('order_page.bookingFee'), amount: bookingFee },
                  ]}
                  totalLabel={t('paymentLinks.amountToPayLabel')}
                  total={remainingBalance + tipAmount + bookingFee}
                  notes={[t('order_page.bookingFeeNonRefundable')]}
                  account={paymentLinkResponse.account}
                />
              </Grid>
            ) : (
              <Grid container item alignItems="center" justifyContent="center" gap={1}>
                <OrderPageTypography>{t('paymentLinks.amountToPay')}</OrderPageTypography>
                <OrderPageTypography variant="h6">
                  {priceFormatterFromCurrency(
                    remainingBalance,
                    paymentLinkResponse.account.currency,
                    undefined,
                    false,
                    false,
                    priceFractionDigits,
                  )}
                </OrderPageTypography>
              </Grid>
            ))}
          {paymentLinkResponse && !isPaidWithGiftCard && !isExpired && (
            <Grid container item alignItems="flex-start" justifyContent="center" gap={1}>
              <OrderPageTextField
                size="small"
                placeholder={t('vouchercode')}
                value={giftCardCode}
                onChange={(event) => setGiftCardCode(event.target.value)}
                error={!!giftCardError}
                helperText={giftCardError}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={!giftCardCode.trim() || isApplyingGiftCard}
                onClick={applyGiftCard}
                sx={{
                  borderColor: theme.customTheme.palette.border,
                  color: theme.customTheme.palette.border,
                  '&:hover': {
                    borderColor: theme.customTheme.palette.primary,
                    color: theme.customTheme.palette.primary,
                    backgroundColor: 'transparent',
                  },
                }}>
                {t('apply')}
              </Button>
            </Grid>
          )}
          {isPaidWithGiftCard && (
            <OrderPageTypography variant="h6">{t('paymentLinks.paidWithGiftCard')}</OrderPageTypography>
          )}
        </Grid>

        {paymentLinkResponse && !isPaidWithGiftCard && !isExpired && (
          <>
            {paymentTimerBox}
            <Divider sx={{ width: '100%', mb: 3 }} />
            <Grid container flexDirection="column" gap={2} width="100%">
              <TermsConditionsCheckbox
                account={account}
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
              {mandatoryBoolFields.map((field: CustomPersonalDetailData) => (
                <FormControlLabel
                  key={field.id}
                  control={
                    <OrderPageCheckbox
                      checked={boolFieldsChecked[field.id]}
                      onChange={(e) => setBoolFieldsChecked((prev) => ({ ...prev, [field.id]: e.target.checked }))}
                    />
                  }
                  label={
                    <Typography sx={{ color: theme.customTheme.palette.text.primary }}>
                      {field.label[language]}
                    </Typography>
                  }
                />
              ))}
            </Grid>
            <Divider sx={{ width: '100%', mt: 3, mb: 2 }} />
            <Grid container justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                disabled={!allChecked}
                onClick={() => {
                  if (shouldEnablePaymentsSelection) setShouldShowPaymentsSelector(true);
                  setCurrentStep(1);
                }}
                sx={{
                  borderColor: theme.customTheme.palette.border,
                  color: theme.customTheme.palette.border,
                  '&:hover': {
                    borderColor: theme.customTheme.palette.primary,
                    color: theme.customTheme.palette.primary,
                    backgroundColor: 'transparent',
                  },
                }}>
                {t('paymentLinks.proceedToPayment')}
              </Button>
            </Grid>
          </>
        )}

        {error !== Errors.NO_ERROR && (
          <Fade in timeout={1000}>
            <Alert sx={{ width: '230px', mt: 3 }} severity="error" variant="outlined">
              <OrderPageTypography color="error">{getErrorText(error)}</OrderPageTypography>
            </Alert>
          </Fade>
        )}
      </Grid>
    );
  }

  return (
    <Grid
      container
      flexDirection="column"
      alignContent="center"
      p={isSmallScreen ? 2 : 4}
      alignItems="center"
      gap={isSmallScreen ? 2 : 6}>
      <Grid container item justifyContent="flex-end">
        <Button
          size="small"
          onClick={() => {
            setCurrentStep(0);
            setShouldShowPaymentsSelector(false);
            setSelectedMaxPayments(1);
          }}
          sx={{ color: theme.customTheme.palette.primary }}>
          {t('back')}
        </Button>
      </Grid>
      <AccountLogo account={account} width={120} style={{ width: 120 }} alt="" />
      {paymentTitle && (
        <OrderPageTypography variant="h3">
          {t('paymentLinks.paymentFor')} {paymentTitle}
        </OrderPageTypography>
      )}
      {paymentLinkResponse?.should_show_tip_component && (
        <Grid flexDirection="column" mb={2} alignContent="center">
          <Grid item mb={2}>
            <Divider />
          </Grid>
          <TipComponent tipInitial={tip?.value} setTip={setTipInternal} initialType={tip?.type} />
          {tip?.value && tip?.value > 0 ? (
            <Grid container item gap={2} mt={2}>
              <OrderPageTypography>{t('order_page.gratuity')}:</OrderPageTypography>
              <OrderPageTypography bold>
                {priceFormatterFromCurrency(
                  calculateTip(
                    tip,
                    paymentLinkResponse?.payment_link.amount_due ?? paymentLinkResponse?.payment_link.price,
                  ),
                  paymentLinkResponse?.account.currency,
                  undefined,
                  false,
                  false,
                  priceFractionDigits,
                )}
              </OrderPageTypography>
            </Grid>
          ) : null}
          <Grid item mt={3}>
            <Divider />
          </Grid>
        </Grid>
      )}
      {!isExpired && paymentTimerBox}
      {isExpired ? (
        <Alert sx={{ width: '230px' }} severity="error" variant="outlined">
          <OrderPageTypography color="error">{getErrorText(error)}</OrderPageTypography>
        </Alert>
      ) : shouldShowPaymentsSelector && !parseSquarePaymentForm(paymentLinkResponse?.payment_link.link) ? (
        <Grid container flexDirection="column" alignItems="center" gap={2} mt={2}>
          <Grid container alignItems="center" justifyContent="center" gap={1}>
            <OrderPageTypography variant="h6">{t('order_page.installments')}</OrderPageTypography>
            <OrderPageSelect
              value={selectedMaxPayments}
              onChange={(event) => setSelectedMaxPayments(Number(event.target.value))}
              style={{
                padding: '2px 4px',
                borderRadius: '4px',
                border: `1px solid ${theme.customTheme.palette.border}`,
                width: '100px',
                color: theme.customTheme.palette.text.secondary,
                backgroundColor: 'transparent',
              }}>
              {Array.from({ length: account?.payment_integration_settings?.max_payments || 1 }, (_, i) => i + 1).map(
                (num) => (
                  <FormStyledMenuItem
                    key={`MenuItem-${num}`}
                    value={num}
                    textColor={theme.customTheme.palette.text.primary}
                    backgroundColor={theme.customTheme.palette.background}>
                    <OrderPageTypography>{num}</OrderPageTypography>
                  </FormStyledMenuItem>
                ),
              )}
            </OrderPageSelect>
          </Grid>
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              await getPaymentLink(selectedMaxPayments);
              setShouldShowPaymentsSelector(false);
            }}
            sx={{
              borderColor: theme.customTheme.palette.border,
              color: theme.customTheme.palette.border,
              '&:hover': {
                borderColor: theme.customTheme.palette.primary,
                color: theme.customTheme.palette.primary,
                backgroundColor: 'transparent',
              },
            }}>
            {t('paymentLinks.proceedToPayment')}
          </Button>
        </Grid>
      ) : (
        paymentLinkResponse?.payment_link.link && (
          <Grid container flexDirection="column">
            <PaymentIframe
              paymentUrl={paymentLinkResponse?.payment_link.link}
              stripe_integration_publishable_key={stripe_integration_publishable_key}
              stripeAccountId={stripeAccountId}
              onFormExpired={() => {
                void getPaymentLink();
              }}
            />
          </Grid>
        )
      )}
    </Grid>
  );
};
